import { Injectable, NgZone } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class MapService {
  mapIsInitialized: boolean = false;

  constructor(private _zone: NgZone) {
  }

  // Run a Google Maps callback inside the Angular zone.
  //
  // The Maps API is loaded and evaluated before bootstrapModule (see main.ts) and
  // MarkerClustererPlus is a plain script tag in index.html, so both were bound to the root
  // zone before an NgZone existed. On top of that, addListener is Google's own event bus,
  // which zone.js does not patch at all. So every map callback - and every HTTP call, timer
  // or promise started from one - runs outside Angular: bound state is updated but change
  // detection never ticks, and the template only catches up when some unrelated event
  // happens to enter the zone. That is the "the data arrived but the UI did not move" class
  // of bug, and it is why the marker card used to appear late.
  //
  // Re-enter at the listener itself rather than around the assignment, so work the callback
  // kicks off is scheduled in the zone too. Native await is the one exception: the ES2022
  // target means zone.js cannot patch it, so a callback that awaits has to call this again
  // for its continuation.
  public runInZone<T>(fn: () => T): T {
    return this._zone.run(fn);
  }

  public setCircleColors(circle: google.maps.Circle, isInEditMode: boolean) {
    const strokeColor = isInEditMode === true ? strokeColors.IN_PROCESS : strokeColors.COMPLETED;
    const fillColor = isInEditMode === true ? fillColors.IN_PROCESS : fillColors.COMPLETED;
    circle.setOptions({ strokeColor: strokeColor, fillColor: fillColor });
  }

  // ============================================================================
  // map drawing and filter profile support classes
  // ============================================================================

  public getNewRegionCircleOptions(isInEditMode: boolean = true): google.maps.CircleOptions {
    const strokeColor = isInEditMode === true ? strokeColors.IN_PROCESS : strokeColors.COMPLETED;
    const fillColor = isInEditMode === true ? fillColors.IN_PROCESS : fillColors.COMPLETED;

    return {
      strokeColor: strokeColor,
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: fillColor,
      fillOpacity: .1,
      clickable: false,
      editable: false,
      zIndex: 0,
    };
  }

  // The DrawingManager owns the drag surface, and that surface sits above the marker
  // layer - so markers and cluster icons cannot swallow the mousedown that starts a
  // circle. Google's own drawing toolbar stays permanently hidden; the draw button in
  // the map control panel is what arms drawing.
  public createDrawingManager(map: google.maps.Map): google.maps.drawing.DrawingManager {
    const drawingManager = new google.maps.drawing.DrawingManager({
      drawingControl: false,
      circleOptions: this.getNewRegionCircleOptions(),
    });

    drawingManager.setDrawingMode(null);
    drawingManager.setMap(map);

    return drawingManager;
  }

  public enableDrawingMode(drawingManager: google.maps.drawing.DrawingManager) {
    drawingManager.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE);
  }

  public disableDrawingMode(drawingManager: google.maps.drawing.DrawingManager) {
    drawingManager.setDrawingMode(null);
  }

  public isDrawingModeActive(drawingManager: google.maps.drawing.DrawingManager): boolean {
    return drawingManager != null && drawingManager.getDrawingMode() != null;
  }

  getCircleCenter(circle: google.maps.Circle): google.maps.LatLng {
    const center = circle.getCenter();
    const lat = center.lat();
    const lng = center.lng();
    return new google.maps.LatLng(this.roundNumber(lat, 4), this.roundNumber(lng, 4))
  }

  getCircleRadius(circle: google.maps.Circle): number {
    return this.roundNumber(this.metersToMiles(circle.getRadius()), 2);
  }

  roundNumber(num: number, decimalPlaces: number): number {
    const factor = 10 ** decimalPlaces;
    return Math.round(num * factor) / factor;
  }

  metersToMiles(meters): number {
    return meters / 1609.34
  }

  // ============================================================================
  // map framing (centering/zooming) support
  // ============================================================================

  //A framed area has nothing real to show in two cases, and both mean "this record has no location":
  //  - no points were ever extended into the bounds (nothing was located at all), or
  //  - every point collapses onto the 0,0 sentinel (null island), which is what the platform stores
  //    when a tank/location has never been given coordinates - a "no location" marker, not a place.
  //Note the two have different corners: an empty bounds carries Google's inverted sentinel, not 0,0,
  //so checking the 0,0 corners alone would miss it.
  public hasNoLocation(bounds: google.maps.LatLngBounds): boolean {
    if (bounds.isEmpty()) {
      return true;
    }
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    return ne.lat() === 0 && ne.lng() === 0 && sw.lat() === 0 && sw.lng() === 0;
  }

  //Center and zoom a map on the given bounds. When there are no real coordinates to show, open the
  //widest view instead - fitBounds on an unlocated record would otherwise dive to street level in
  //empty ocean at 0,0.
  public frameBounds(
    map: google.maps.Map,
    bounds: google.maps.LatLngBounds,
    options: MapFramingOptions = {},
  ): void {
    if (!map || !bounds) {
      return;
    }

    if (this.hasNoLocation(bounds)) {
      map.setCenter(new google.maps.LatLng(0, 0));
      map.setZoom(noLocationZoom);
      return;
    }

    if (options.maxAutoZoom != null) {
      map.setOptions({ maxZoom: options.maxAutoZoom });
      google.maps.event.addListenerOnce(map, 'idle', () => {
        map.setOptions({ maxZoom: options.restoreMaxZoom });
      });
    }

    map.fitBounds(bounds);

    if (options.panIntoView) {
      map.panToBounds(bounds);
    }
  }

}

//Zoom used when a map has nothing real to frame. Matches the minZoom every TankLink map declares.
export const noLocationZoom = 2;

export interface MapFramingOptions {
  //Pan the framed bounds fully into view after fitting.
  panIntoView?: boolean;
  //Cap the zoom fitBounds is allowed to reach. Needed when framing a single point, which fitBounds
  //would otherwise take straight to the map's maxZoom (street level). Requires restoreMaxZoom.
  maxAutoZoom?: number;
  //The map's normal maxZoom, restored once the capped fit settles so manual zoom is unaffected.
  restoreMaxZoom?: number;
}
export enum mapRegionTypes {
  LOCATION_BASED = 1,
  LATLON_BASED = 2,
}
export enum regionStatusCodes {
  IN_PROCESS = 1,
  COMPLETED = 2,
}
export enum strokeColors {
  IN_PROCESS = "#333333",
  COMPLETED = "#0000FF",
}
export enum fillColors {
  IN_PROCESS = "#333333",
  COMPLETED = "#00AAFF",
}

export class MapRegion {
  regionType: mapRegionTypes;
  regionStatus: regionStatusCodes;
  circleMapRegion: google.maps.Circle = null;
  map: google.maps.Map;

  constructor(
    map: google.maps.Map,
    regionType: mapRegionTypes,
    regionStatus: regionStatusCodes,
    opts?: any,
    region: any = null
  ) {
    // create the object based on the specific Google Maps object type
    if (map === null)
      return;

    this.map = map
    this.regionType = regionType;
    this.regionStatus = regionStatus;

    if (region) {
      // we already have a map region object in place
      switch (this.regionType) {
        case mapRegionTypes.LOCATION_BASED:
        case mapRegionTypes.LATLON_BASED:
          {
            this.circleMapRegion = region;
            break;
          }
      }
      return;
    }

    switch (this.regionType) {
      case mapRegionTypes.LOCATION_BASED:
      case mapRegionTypes.LATLON_BASED:
        {
          // create a circular region
          this.circleMapRegion = this.createMapCircle(opts.centerLat, opts.centerLon, opts.radius, this.regionStatus);
          break;
        }
    }

    if (this.regionStatus == regionStatusCodes.COMPLETED)
      this.drawMapRegion(opts);
  }

  // create a circle map region w/o drawing it
  public createMapCircle(centerLat: number, centerLon: number, radius: number, regionStatus: regionStatusCodes): google.maps.Circle {
    var circle = new google.maps.Circle({
      strokeColor: this.getStrokeColor(regionStatus),
      strokeOpacity: 0.8,
      strokeWeight: 2,
      fillColor: this.getFillColor(regionStatus),
      fillOpacity: .1,
      center: { lat: centerLat, lng: centerLon },
      radius: this.metersPerMile(radius),
    });
    return circle;
  }

  // draw the map region
  public drawMapRegion(opts: any) {
    // draw the map region
    switch (this.regionType) {
      case mapRegionTypes.LOCATION_BASED:
      case mapRegionTypes.LATLON_BASED:
        {
          // draw a circular region
          this.circleMapRegion.setMap(this.map)
          break;
        }
    }
  }

  removeMapRegion() {
    switch (this.regionType) {
      case mapRegionTypes.LOCATION_BASED:
      case mapRegionTypes.LATLON_BASED:
        {
          this.circleMapRegion.setMap(null)
          this.circleMapRegion = null;
          break;
        }
    }
  }

  getMapRegionBounds(): google.maps.LatLngBounds {
    switch (this.regionType) {
      case mapRegionTypes.LOCATION_BASED:
      case mapRegionTypes.LATLON_BASED:
        {
          return this.circleMapRegion.getBounds();
          break;
        }
    }
    return new google.maps.LatLngBounds();
  }

  metersPerMile(radius: number): number {
    return radius * 1609.34;
  }

  getStrokeColor(regionStatus: regionStatusCodes): string {
    return regionStatus === regionStatusCodes.IN_PROCESS ? strokeColors.IN_PROCESS : strokeColors.COMPLETED;
  }

  getFillColor(regionStatus: regionStatusCodes): string {
    return regionStatus === regionStatusCodes.IN_PROCESS ? fillColors.IN_PROCESS : fillColors.COMPLETED;
  }
}
